import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Button from '../../../components/ui/Button';
import PhoneInput from './PhoneInput';
import OtpInput from './OtpInput';

const LoginForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useForm();

  // Mock credentials for testing
  const MOCK_PHONE = '989120000000';
  const MOCK_OTP = '123456';

  const validatePhone = (phone) => {
    if (!phone) {
      return 'شماره موبایل الزامی است';
    }

    if (phone?.length !== 12) {
      return 'شماره موبایل باید ۱۲ رقم باشد';
    }

    if (!phone?.startsWith('98')) {
      return 'شماره موبایل باید با کد کشور ایران (+۹۸) شروع شود';
    }

    const iranMobilePrefixes = [
      '901',
      '902',
      '903',
      '905',
      '910',
      '911',
      '912',
      '913',
      '914',
      '915',
      '916',
      '917',
      '918',
      '919',
      '920',
      '921',
      '922',
      '934',
      '935',
      '936',
      '937',
      '938',
      '939',
    ];
    const prefix = phone?.slice(2, 5);

    if (!iranMobilePrefixes?.includes(prefix)) {
      return 'شماره موبایل معتبر نیست';
    }

    return null;
  };

  const validateOtp = (otp) => {
    if (!otp) {
      return 'کد تأیید الزامی است';
    }

    if (otp?.length !== 6) {
      return 'کد تأیید باید ۶ رقم باشد';
    }

    return null;
  };

  const handlePhoneSubmit = async (e) => {
    e?.preventDefault();
    clearErrors();

    const phoneError = validatePhone(phoneNumber);
    if (phoneError) {
      setError('phone', { message: phoneError });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (phoneNumber === MOCK_PHONE) {
        setStep('otp');
        toast?.success('کد تأیید ارسال شد', {
          description: 'کد ۶ رقمی به شماره موبایل شما ارسال شد',
        });
      } else {
        setError('phone', { message: 'شماره موبایل در سیستم یافت نشد' });
        toast?.error('خطا در ورود', {
          description: 'شماره موبایل وارد شده معتبر نیست',
        });
      }
    } catch (error) {
      toast?.error('خطا در ارسال کد', {
        description: 'لطفاً مجدداً تلاش کنید',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e?.preventDefault();
    clearErrors();

    const otpError = validateOtp(otpCode);
    if (otpError) {
      setError('otp', { message: otpError });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (otpCode === MOCK_OTP) {
        // Store auth token in localStorage
        const mockToken =
          'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
        localStorage.setItem('authToken', mockToken);
        localStorage.setItem('userPhone', phoneNumber);

        toast?.success('ورود موفقیت‌آمیز', {
          description: 'به پنل کاربری خوش آمدید',
        });

        navigate('/dashboard-overview');
      } else {
        setError('otp', { message: 'کد تأیید اشتباه است' });
        toast?.error('کد تأیید نادرست', {
          description: 'کد وارد شده صحیح نمی‌باشد',
        });
      }
    } catch (error) {
      toast?.error('خطا در تأیید کد', {
        description: 'لطفاً مجدداً تلاش کنید',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setOtpCode('');
    clearErrors();
  };

  if (step === 'otp') {
    return (
      <form onSubmit={handleOtpSubmit} className="space-y-6">
        <OtpInput
          value={otpCode}
          onChange={setOtpCode}
          error={errors?.otp?.message}
          disabled={isLoading}
        />
        <div className="space-y-3">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={otpCode?.length !== 6}>
            {isLoading ? 'در حال تأیید...' : 'تأیید و ورود'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="lg"
            fullWidth
            onClick={handleBackToPhone}
            disabled={isLoading}>
            بازگشت به مرحله قبل
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handlePhoneSubmit} className="space-y-6">
      <PhoneInput
        value={phoneNumber}
        onChange={setPhoneNumber}
        error={errors?.phone?.message}
        disabled={isLoading}
      />
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={!phoneNumber}>
        {isLoading ? 'در حال ارسال...' : 'ارسال کد تأیید'}
      </Button>
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          با ورود به سیستم، شما{' '}
          <button type="button" className="text-primary hover:underline">
            قوانین و مقررات
          </button>{' '}
          را می‌پذیرید
        </p>
      </div>
    </form>
  );
};

export default LoginForm;
